import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useAuth } from '@/auth/hooks/useAuth';
import { setAuthFlash, clearAuthFlash } from '@/lib/auth-flash';
import { DeleteHistoryDialog } from '@/auth/components/DeleteHistoryDialog';
import { ConnectedAppsCard } from '@/auth/components/ConnectedAppsCard';
import { CompanyPortalsCard } from '@/auth/components/CompanyPortalsCard';
import { AiMemoryCard } from '@/auth/components/AiMemoryCard';
import { MfaSettingsCard } from '@/auth/components/MfaSettingsCard';
import { PasskeysCard } from '@/auth/components/PasskeysCard';
import { ApiKeysCard } from '@/auth/components/ApiKeysCard';
import api from '@/lib/api';

export function AccountSettingsPage() {
  const { user, signOut, changePassword } = useAuth();
  const navigate = useNavigate();

  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');
  // Reveal the TOTP field if the backend says a code is required, even when the
  // token's `mfaEnrolled` hint is stale (e.g. enrolled in another tab).
  const [mfaCodeRequired, setMfaCodeRequired] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  // Enrolled accounts must supply a current authenticator code to change the
  // password (backend step-up). Show the field when the hint says so, or once
  // the backend has told us it's required.
  const needsTotp = (user?.mfaEnrolled ?? false) || mfaCodeRequired;

  // Delete message history state
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDeletingHistory, setIsDeletingHistory] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [deleteSuccess, setDeleteSuccess] = useState('');

  const validatePassword = (pwd: string): string | null => {
    if (pwd.length < 8) return 'Password must be at least 8 characters';
    if (!/[A-Z]/.test(pwd)) return 'Password must contain an uppercase letter';
    if (!/[a-z]/.test(pwd)) return 'Password must contain a lowercase letter';
    if (!/[0-9]/.test(pwd)) return 'Password must contain a number';
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(pwd)) return 'Password must contain a special character';
    return null;
  };

  const handleChangePassword = async (e: FormEvent) => {
    e.preventDefault();
    setPasswordError('');

    if (newPassword !== confirmPassword) {
      setPasswordError('Passwords do not match');
      return;
    }

    const validationError = validatePassword(newPassword);
    if (validationError) {
      setPasswordError(validationError);
      return;
    }

    if (needsTotp && totpCode.length !== 6) {
      setPasswordError('Enter the 6-digit code from your authenticator app.');
      return;
    }

    setIsChangingPassword(true);

    // A successful change revokes the Cognito session, so AuthContext clears
    // `user` and ProtectedRoute immediately redirects to /auth/login. Stage
    // the confirmation *before* that happens (the flash must exist when
    // LoginPage mounts) so the user isn't dumped on a blank login page. On
    // failure we clear it and show the inline error instead — no redirect
    // occurs in that case.
    setAuthFlash('Password changed. Please sign in with your new password.');
    try {
      await changePassword(oldPassword, newPassword, needsTotp ? totpCode : undefined);
    } catch (err) {
      clearAuthFlash();
      const code =
        typeof err === 'object' && err !== null && 'code' in err
          ? (err as { code?: string }).code
          : undefined;
      if (code === 'MFA_CODE_REQUIRED') {
        // Stale hint: MFA is on but the token didn't say so. Reveal the field.
        setMfaCodeRequired(true);
        setPasswordError(
          'Enter the 6-digit code from your authenticator app to change your password.',
        );
      } else if (code === 'MFA_CODE_MISMATCH') {
        setPasswordError('That authentication code was incorrect. Try a fresh code.');
      } else {
        setPasswordError(err instanceof Error ? err.message : 'Failed to change password');
      }
    } finally {
      setIsChangingPassword(false);
    }
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/auth/login');
  };

  const handleDeleteHistory = async () => {
    setDeleteError('');
    setDeleteSuccess('');
    setIsDeletingHistory(true);

    try {
      const result = await api.deleteAllConversations();
      setDeleteSuccess(
        `Successfully deleted ${result.deletedCount} conversation${result.deletedCount !== 1 ? 's' : ''}.`,
      );
      setIsDeleteDialogOpen(false);
      // Notify any open AI pages to clear their current messages
      window.dispatchEvent(new Event('fias:history-cleared'));
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : 'Failed to delete message history');
    } finally {
      setIsDeletingHistory(false);
    }
  };

  return (
    <div>
      {/* Page Header */}
      <div className="border-b border-border bg-card">
        <div className="max-w-4xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
          <h1 className="text-2xl font-bold text-foreground">Account Settings</h1>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8 sm:px-6 lg:px-8 space-y-6">
        {/* Profile Information */}
        <Card>
          <CardHeader>
            <CardTitle>Profile Information</CardTitle>
            <CardDescription>Your account details</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-1">
              <Label className="text-muted-foreground">Username</Label>
              <p className="text-foreground">{user?.username || 'Loading...'}</p>
            </div>
            <div className="grid gap-1">
              <Label className="text-muted-foreground">Email</Label>
              <p className="text-foreground">{user?.email || 'Loading...'}</p>
            </div>
            <div className="grid gap-1">
              <Label className="text-muted-foreground">User ID</Label>
              <p className="text-foreground font-mono text-sm">{user?.userId || 'Loading...'}</p>
            </div>
            <div className="grid gap-1">
              <Label className="text-muted-foreground">Roles</Label>
              <div className="flex gap-2">
                {user?.roles?.map((role) => (
                  <span
                    key={role}
                    className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary"
                  >
                    {role}
                  </span>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Change Password */}
        <Card>
          <CardHeader>
            <CardTitle>Change Password</CardTitle>
            <CardDescription>Update your password to keep your account secure</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleChangePassword} className="space-y-4">
              {passwordError && (
                <div className="rounded-md bg-destructive/10 border border-destructive/20 p-4">
                  <p className="text-sm text-destructive">{passwordError}</p>
                </div>
              )}

              {/*
                Username anchor for the browser's password manager. With the
                logged-in account's email present (autocomplete="username"),
                the manager fills THIS account's current password rather than
                whichever saved login it picks by origin alone, and updates the
                correct saved entry after a successful change. Read-only — it's
                context confirming which account you're editing, not an input.
                Rendered unconditionally (even before `user` loads) so it's in
                the DOM for the browser's initial autofill pass.
              */}
              <div className="space-y-2">
                <Label htmlFor="change-password-username">Account</Label>
                <Input
                  id="change-password-username"
                  name="username"
                  type="text"
                  autoComplete="username"
                  value={user?.email ?? ''}
                  readOnly
                  tabIndex={-1}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="old-password">Current password</Label>
                <PasswordInput
                  id="old-password"
                  value={oldPassword}
                  onChange={(e) => setOldPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                  disabled={isChangingPassword}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="new-password">New password</Label>
                <PasswordInput
                  id="new-password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  autoComplete="new-password"
                  disabled={isChangingPassword}
                />
                <p className="text-xs text-muted-foreground">
                  At least 8 characters with uppercase, lowercase, number, and special character.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirm-new-password">Confirm new password</Label>
                <PasswordInput
                  id="confirm-new-password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  autoComplete="new-password"
                  disabled={isChangingPassword}
                />
              </div>

              {needsTotp && (
                <div className="space-y-2">
                  <Label htmlFor="change-password-totp">Authentication code</Label>
                  <Input
                    id="change-password-totp"
                    type="text"
                    inputMode="numeric"
                    placeholder="123456"
                    value={totpCode}
                    onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    autoComplete="one-time-code"
                    maxLength={6}
                    disabled={isChangingPassword}
                    className="font-mono tracking-[0.3em]"
                  />
                  <p className="text-xs text-muted-foreground">
                    Two-factor authentication is on — enter a current code from your authenticator
                    app.
                  </p>
                </div>
              )}

              <Button type="submit" disabled={isChangingPassword}>
                {isChangingPassword ? 'Changing...' : 'Change password'}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Two-Factor Authentication */}
        <MfaSettingsCard />

        <PasskeysCard />

        {/* Delete Message History */}
        <Card>
          <CardHeader>
            <CardTitle>Message History</CardTitle>
            <CardDescription>Manage your conversation history across all AI arches</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {deleteError && (
              <div className="rounded-md bg-destructive/10 border border-destructive/20 p-4">
                <p className="text-sm text-destructive">{deleteError}</p>
              </div>
            )}
            {deleteSuccess && (
              <div className="rounded-md bg-green-500/10 border border-green-500/20 p-4">
                <p className="text-sm text-green-600 dark:text-green-400">{deleteSuccess}</p>
              </div>
            )}

            <p className="text-sm text-muted-foreground">
              Delete all conversation history from Single-AI and Multi-AI arches. This will
              permanently remove all your messages and cannot be undone.
            </p>

            <Button variant="outline" onClick={() => setIsDeleteDialogOpen(true)}>
              Delete all message history
            </Button>
          </CardContent>
        </Card>

        {/* Connected Apps */}
        <ConnectedAppsCard />
        <CompanyPortalsCard />

        <AiMemoryCard />

        {/* API keys for external AI agents (MCP) */}
        <ApiKeysCard />

        {/* Account Actions */}
        <Card>
          <CardHeader>
            <CardTitle>Account Actions</CardTitle>
            <CardDescription>Sign out of your account</CardDescription>
          </CardHeader>
          <CardContent>
            <Button variant="outline" onClick={handleSignOut}>
              Sign out
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Delete History Confirmation Dialog */}
      <DeleteHistoryDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        onConfirm={handleDeleteHistory}
        isDeleting={isDeletingHistory}
      />
    </div>
  );
}
